/**
 * Isolated refreshToken API call for new httpOnly auth flow
 *
 * Based on services/api/accessToken/refreshToken.js with modifications:
 * - Uses newAuthUfeApi.makeRequest instead of native fetch
 * - NO Seph-Access-Token header (x-api-key used by newAuthUfeApi)
 * - Empty request body (tokens sent via httpOnly cookie)
 * - Returns profileStatus in response
 * - Stores profileSecurityStatus and isSSI to localStorage
 */
import newAuthUfeApi from 'services/auth/newFlow/api/newAuthUfeApi';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import userUtils from 'utils/User';

const REFRESH_TOKEN_URL = '/gway/v1/dotcom/auth/v2/refreshToken';
const BACKOFF_DELAYS = [2000, 4000, 6000]; // Retry delays in ms

async function makeRequestWithTokens(payload, headers, url) {
    return newAuthUfeApi
        .makeRequest(
            url,
            {
                url,
                method: 'POST',
                body: JSON.stringify(payload),
                headers
            },
            {
                skipTokenCheck: true // CRITICAL: Prevent infinite loop (refreshToken shouldn't check tokens before calling itself)
            }
        )
        .then(data => {
            if (data.errorCode || data.errors) {
                return Promise.reject(data);
            }

            // Store profileStatus in localStorage (0, 2, 3, or 4)
            // Tokens are NOT stored - they're in httpOnly cookies
            data.profileSecurityStatus != null && Storage.local.setItem(LOCAL_STORAGE.PROFILE_SECURITY_STATUS, data.profileSecurityStatus);

            // Store to both isSSI (new) and staySignIn (old) for backward compatibility
            if (data.isSSI != null) {
                Storage.local.setItem(LOCAL_STORAGE.IS_SSI, data.isSSI);
                Storage.local.setItem(LOCAL_STORAGE.STAY_SIGN_IN, data.isSSI);
            }

            // Store tokens for backward compatibility (to be removed later)
            // once BE is fully on httpOnly cookie flow
            if (data.accessToken) {
                Storage.local.setItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN, data.accessToken);
            }

            if (data.refreshToken) {
                Storage.local.setItem(LOCAL_STORAGE.AUTH_REFRESH_TOKEN, data.refreshToken);
            }

            return data;
        });
}

async function buildRequestConfig(options = {}) {
    const url = REFRESH_TOKEN_URL;

    let headers = {
        'Content-Type': 'application/json',
        'x-requested-source': 'web'
    };

    // Merge any additional headers from options
    if (options.headers) {
        headers = {
            ...headers,
            ...options.headers
        };
    }

    // For httpOnly flow: empty payload (tokens come from cookie)
    // Only include email if provided (for SSI flows)
    const email = options.email || userUtils.getProfileEmail();
    const payload = email ? { email } : {};

    return { url, headers, payload };
}

async function refreshToken(bodyJSON = {}, options = {}, retryCount = 0) {
    const { url, headers, payload } = await buildRequestConfig({ ...bodyJSON, ...options });

    try {
        return await makeRequestWithTokens(payload, headers, url);
    } catch (error) {
        // Retry logic with exponential backoff
        if (retryCount < BACKOFF_DELAYS.length) {
            const delay = BACKOFF_DELAYS[retryCount];

            Sephora.logger.info(`[refreshToken] Retrying (attempt ${retryCount + 1}/${BACKOFF_DELAYS.length}) after ${delay}ms`, {
                error: error.message || error
            });

            await new Promise(resolve => setTimeout(resolve, delay));

            return refreshToken(bodyJSON, options, retryCount + 1);
        }

        // No more retries - throw error to trigger login modal
        Sephora.logger.error('[refreshToken] All retry attempts exhausted', {
            retryCount,
            error: error.message || error
        });

        throw error;
    }
}

export default {
    refreshToken
};
